import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const KINDS = ["Note", "Template", "Fabrication", "Install", "Service", "Material", "Arrival", "Viewing"];
const WINDOWS = ["All day", "AM", "PM"];

const blank = (date) => ({
  title: "",
  kind: "Note",
  date: date || "",
  time: "",
  window: "All day",
  crew: "",
  address: "",
  customer_name: "",
  notes: "",
});

export function CalendarEventDialog({ open, onOpenChange, event, defaultDate, onSaved }) {
  const editing = !!event?.id;
  const [form, setForm] = useState(blank(defaultDate));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: event.title || "",
        kind: event.kind || "Note",
        date: event.date || defaultDate || "",
        time: event.time || "",
        window: event.window || "All day",
        crew: event.crew || "",
        address: event.address || "",
        customer_name: event.customer_name || "",
        notes: event.notes || "",
      });
    } else {
      setForm(blank(defaultDate));
    }
  }, [open, editing, event, defaultDate]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Give the event a title");
      return;
    }
    if (!form.date) {
      toast.error("Pick a date");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/calendar/events/${event.id}`, form);
        toast.success("Event updated");
      } else {
        await api.post("/calendar/events", form);
        toast.success("Event added to the calendar");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/calendar/events/${event.id}`);
      toast.success("Event removed");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="calendar-event-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit event" : "Add event"}</DialogTitle>
          <DialogDescription>
            Drop a custom entry on the schedule — a reminder, a block, or anything your crew needs to see.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="cal-ev-title">Title</Label>
            <Input
              id="cal-ev-title"
              data-testid="calendar-event-title"
              value={form.title}
              onChange={(e) => set("title")(e.target.value)}
              placeholder="e.g. Shop deep-clean"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={set("kind")}>
                <SelectTrigger data-testid="calendar-event-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="cal-ev-date">Date</Label>
              <Input
                id="cal-ev-date"
                type="date"
                data-testid="calendar-event-date"
                value={form.date}
                onChange={(e) => set("date")(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="cal-ev-time">Time</Label>
              <Input
                id="cal-ev-time"
                type="time"
                data-testid="calendar-event-time"
                value={form.time}
                onChange={(e) => set("time")(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Window</Label>
              <Select value={form.window} onValueChange={set("window")}>
                <SelectTrigger data-testid="calendar-event-window">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOWS.map((w) => (
                    <SelectItem key={w} value={w}>
                      {w}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="cal-ev-crew">Crew / installer</Label>
              <Input
                id="cal-ev-crew"
                data-testid="calendar-event-crew"
                value={form.crew}
                onChange={(e) => set("crew")(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="cal-ev-customer">Customer</Label>
              <Input
                id="cal-ev-customer"
                data-testid="calendar-event-customer"
                value={form.customer_name}
                onChange={(e) => set("customer_name")(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="cal-ev-address">Address</Label>
            <Input
              id="cal-ev-address"
              data-testid="calendar-event-address"
              value={form.address}
              onChange={(e) => set("address")(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="cal-ev-notes">Notes</Label>
            <Textarea
              id="cal-ev-notes"
              data-testid="calendar-event-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              placeholder="Optional details"
            />
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          {editing ? (
            <Button
              type="button"
              variant="outline"
              data-testid="calendar-event-delete"
              onClick={remove}
              disabled={saving}
              className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
            >
              <Trash size={15} weight="bold" className="mr-1.5" /> Delete
            </Button>
          ) : (
            <span />
          )}
          <Button
            type="button"
            data-testid="calendar-event-save"
            onClick={save}
            disabled={saving}
            className="bg-indigo-500 text-white hover:bg-indigo-400"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Add event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
